/**
 * @file Analytics helpers (e.g. event listeners and triggers, data transformation, and anything
 *       related to interfacing with our wvus-datalayer-js library).
 */

import { wvusLogger } from "./logging";
import appSettings from "../../settings/appSettings";
import { getIsKioskMode } from "../client-state/kiosk";
import {
  calculateCheckoutCompletionTime,
  calculatePaymentFormCompletionTime,
  calculatePostPurchaseTaskCompletionTime,
  clearCheckoutFormContactableFlag,
  getAddressAutoCompleteUsed,
  getBrowserAutoFillUsed,
  getCheckoutFormViewCount,
  setPostPurchaseStartTimestamp
} from "./observers";
import { retrieveSakQuestionnaireData } from "../client-state/sakLocalStorage";
import { formatSakTopics } from "./analyticsUtils";

const { appType } = appSettings;

const eventNameDefault = appType === "chosen" ? "streamline_checkout" : "flex_checkout";

export const triggerAnalyticsEvent = (eventName = eventNameDefault, eventData = {}) => {
  const event = new CustomEvent(eventName, {
    detail: eventData
  });
  document.dispatchEvent(event);
};

export const getDataLayerPageName = () => {
  return globalThis?.dataLayer?.find(({ page_name }) => page_name)?.page_name || "";
};

export const getCheckoutFlowVal = () => {
  const { checkoutFlow } = appSettings;
  const checkoutFlowVal = {
    PhotoFirst: "photo_first",
    PaymentFirst: "payment_first"
  }[checkoutFlow];

  return checkoutFlowVal;
};

export const getCheckoutTypeVal = (products) =>
  products?.length ? `flex_checkout:${products[0].sku}` : "flex_checkout";

export const mapEventData = (products, dataTransform = (data) => data, includeItemName = false) => {
  const parsePrice = (product) => {
    return +product.price || +product.amount || +product.amt || +product.total || 0;
  };

  const parseItemVariant = (product) => {
    const variant =
      product.item_variant ||
      product.variant ||
      product.frequency ||
      product.uomcode ||
      product.pa_uom;
    const [ONE_TIME, MONTHLY, YEARLY] = ["one-time", "monthly", "yearly"];
    const DEFAULT_VARIANT = ONE_TIME;

    if (typeof variant !== "string") {
      return "one-time";
    }

    const frequencyMap = {
      monthly: MONTHLY,
      mon: MONTHLY,
      yearly: YEARLY,
      yr: YEARLY,
      annually: YEARLY,
      one: ONE_TIME,
      once: ONE_TIME,
      onetime: ONE_TIME
    };

    const variantKey = variant.toLowerCase();
    const frequency = frequencyMap[variantKey] || DEFAULT_VARIANT;
    const price = parsePrice(product) || "";

    return `${frequency}:${price}`;
  };

  // Conditionally Add Property To Object
  const parseVal = (key, all, product, productKey = key) => {
    if (all[key] || product[productKey]) {
      return { [key]: all[key] || product[productKey] };
    }

    return {};
  };

  const eventData = products.reduce(
    (all, product) => {
      return {
        ...all,
        ...parseVal("keyword", all, product?.meta || {}, "wvus_product_event_keyword"), // keyword
        ...parseVal("cobrand", all, product?.meta || {}, "wvus_product_event_cobrand"), // cobrand
        cartProducts: [
          ...all.cartProducts,
          {
            item_name: product.item_name || product.name || product.productname,
            item_id: product.item_id || product.sku || product.id,
            item_brand: product.item_brand || product.brand || "",
            item_variant: parseItemVariant(product),
            item_category: product.item_category || product.category,
            quantity: product.quantity || product.qty || 1,
            price: parsePrice(product)
          }
        ],
        cartTotal: all.cartTotal + parsePrice(product)
      };
    },
    { cartProducts: [], cartTotal: 0 }
  );

  if (includeItemName === true) {
    eventData.item_name = eventData?.cartProducts[0]?.item_name;
  }

  const prefix = appType === "chosen" ? "streamline" : "flex";
  if (getIsKioskMode()) {
    eventData.checkout_type = `${prefix}_checkout_kiosk`;
  } else {
    eventData.checkout_type = `${prefix}_checkout`;
  }

  // If Sponsorship Product
  if (products.length && products[0] && products[0].countryName) {
    eventData.child_country_name = products[0].countryName;
    eventData.child_community_name = products[0].projectName;
    eventData.child_id = products[0]?.meta?.wvus_product_child_identifier;
    eventData.checkout_type += `:${products[0].sku}`;
  }

  if (appType === "chosen") {
    eventData.checkout_type += `:${getCheckoutFlowVal()}`;
  }

  return dataTransform(eventData);
};

const triggerKeys: string[] = [];

export const getSponViewItemData = (product, eventData) => {
  return {
    ...eventData,
    event_action: "flex_child_tile_view",
    event_category: "child_sponsorship",
    event_label: "child_sponsorship"
  };
};

export const getViewItemData = (product, eventData) => {
  if (product?.countryName) {
    return getSponViewItemData(product, eventData);
  }

  return {
    ...eventData
  };
};

export const triggerEvent = (eventTriggerKey, data) => {
  const { products } = data;
  const defaultDataTransform = (data) => data;
  const includeItemName = true;
  const prefix = appType === "chosen" ? "streamlined" : "flex";
  const { sponsoredChildData } = appSettings;

  switch (eventTriggerKey) {
    // With the alt pay options, the user can click and cancel and thus "begin" multiple times.
    case "begin_checkout_alt_pay":
      triggerAnalyticsEvent(
        `${prefix}_checkout_begin_checkout_alt_pay`,
        mapEventData(products, (eventData) => {
          return {
            ...eventData,
            event_label: `${data.eventLabel}`
          };
        })
      );
      break;
    case "quantity_add_to_cart":
      triggerAnalyticsEvent(
        `${prefix}_checkout_add_to_cart`,
        mapEventData([products[products.length - 1]], defaultDataTransform, includeItemName)
      );
      break;
    case "quantity_remove_from_cart":
      triggerAnalyticsEvent(
        `${prefix}_checkout_remove_from_cart`,
        mapEventData([products[products.length - 1]], defaultDataTransform, includeItemName)
      );
      break;
    case "error_view":
      data = { ...data, event_action: "error_view", event_category: "flex_checkout_error" };
      if (appType === "chosen" && data.checkout_type) {
        data.checkout_type += `:${getCheckoutFlowVal()}`;
      }
      console.error(data);
      triggerAnalyticsEvent("error_view", data);
      wvusLogger.log(
        new Error(
          `Flex checkout error reported to analytics with this data: ${JSON.stringify(data)} `
        ),
        wvusLogger.getLogLevelNames().ERROR
      );
      break;
    case "sak_message_compose_view": {
      triggerAnalyticsEvent(`flex_checkout_sak_message_compose_view`, {
        ...data,
        sponsoredChildData
      });
      break;
    }
    case "sak_message_compose_start": {
      triggerAnalyticsEvent(`flex_checkout_sak_message_compose_start`, {
        ...data,
        sponsoredChildData
      });
      break;
    }
    case "sak_message_error": {
      triggerAnalyticsEvent("flex_checkout_sak_message_error", {
        ...data,
        sponsoredChildData
      });
      break;
    }
    default:
      wvusLogger.log(
        new Error(`Datalayer event trigger key ${eventTriggerKey} not handled.`),
        wvusLogger.getLogLevelNames().ERROR
      );
  }
};

export const triggerEventOnce = (eventTriggerKey, data) => {
  const { products } = data;
  const pageName = getDataLayerPageName();
  const addEventLabel = (eventData) => {
    return {
      ...eventData,
      event_label: pageName
    };
  };
  const defaultDataTransform = (data) => data;
  const prefix = appType === "chosen" ? "streamlined" : "flex";
  const { sponsoredChildData } = appSettings;

  if (triggerKeys.includes(eventTriggerKey)) {
    // Only Fire Once
    return;
  }

  triggerKeys.push(eventTriggerKey);
  const includeItemName = true;

  switch (eventTriggerKey) {
    case "view_item":
      triggerAnalyticsEvent(
        `flex_checkout_view_item`,
        mapEventData([products[products.length - 1]], (eventData) => {
          return getViewItemData(products[0], eventData);
        })
      );
      break;
    case "add_to_cart":
      triggerAnalyticsEvent(
        `${prefix}_checkout_add_to_cart`,
        mapEventData([products[products.length - 1]], defaultDataTransform, includeItemName)
      );
      break;
    case "remove_from_cart":
      triggerAnalyticsEvent(
        `${prefix}_checkout_remove_from_cart`,
        mapEventData([products[products.length - 1]], defaultDataTransform, includeItemName)
      );
      break;
    case "begin_checkout":
      triggerAnalyticsEvent(
        `${prefix}_checkout_begin_checkout`,
        mapEventData(products, addEventLabel)
      );
      break;
    // We differentiate alt_pay_view_ event keys so we can send each once and only once.
    case "alt_pay_view_applepay":
      triggerAnalyticsEvent(
        `${prefix}_checkout_alt_pay_view`,
        mapEventData(products, (eventData) => {
          return {
            ...eventData,
            event_label: "applepay"
          };
        })
      );
      break;
    case "alt_pay_view_paypal":
      triggerAnalyticsEvent(
        `${prefix}_checkout_alt_pay_view`,
        mapEventData(products, (eventData) => {
          return {
            ...eventData,
            event_label: "paypal"
          };
        })
      );
      break;
    case "add_billing_address":
      triggerAnalyticsEvent(
        `${prefix}_checkout_add_billing_address`,
        mapEventData(products, (eventData) => {
          return {
            ...eventData,
            event_label: `${pageName}:${data.eventLabel}`
          };
        })
      );
      break;
    case "add_payment_info":
      triggerAnalyticsEvent(
        `${prefix}_checkout_add_payment_info`,
        mapEventData(products, addEventLabel)
      );
      break;
    case "purchase": {
      // Clear the contactable flag after purchase, as this represents the end of the checkout session.
      clearCheckoutFormContactableFlag();

      // This will be used later in sak_message_send_success to calculate the time taken to complete the post-checkout process.
      setPostPurchaseStartTimestamp();

      const purchaseData = mapEventData(products, (eventData) => {
        return {
          ...eventData,
          checkout_form_view_count: getCheckoutFormViewCount(true),
          checkout_completion_time: calculateCheckoutCompletionTime(),
          checkout_form_completion_time: calculatePaymentFormCompletionTime(),
          address_autocomplete: getAddressAutoCompleteUsed(),
          browser_autofill: getBrowserAutoFillUsed(),
          event_label: appType === "chosen" ? "chosen_sponsorship" : "sponsorship"
        };
      });

      triggerAnalyticsEvent(`${prefix}_checkout_purchase`, { ...purchaseData, ...data });
      break;
    }
    case "checkout_form_view":
      triggerAnalyticsEvent("flex_checkout_form_view", {
        ...data,
        event_category: "checkout_form",
        event_action: "checkout_form_view",
        event_label: pageName
      });
      break;
    case "checkout_form_contactable":
      triggerAnalyticsEvent("flex_checkout_form_contactable", {
        ...data,
        event_category: "checkout_form",
        event_action: "checkout_form_contactable",
        event_label: pageName
      });
      break;
    case "sak_message_send_success": {
      const sakQestionnaireData = retrieveSakQuestionnaireData();
      const topics = formatSakTopics(sakQestionnaireData);
      triggerAnalyticsEvent(`flex_checkout_sak_message_send_success`, {
        ...data,
        topics,
        post_purchase_task_completion_time: calculatePostPurchaseTaskCompletionTime(),
        sponsoredChildData
      });
      break;
    }
    default:
      wvusLogger.log(
        new Error(`Datalayer event trigger key ${eventTriggerKey} not handled.`),
        wvusLogger.getLogLevelNames().ERROR
      );
  }
};
